import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { importFreeTimerEntries } from "@/utils/freeTimerImport";
import { useAuth } from "@/contexts/AuthContext";
import InboundPendingBanner from "@/components/InboundPendingBanner";
import InvestmentAllowanceSlider from "@/components/InvestmentAllowanceSlider";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ArrowUpRight,
  ArrowDownRight,
  FileText,
  Receipt,
  Clock,
  AlertTriangle,
  ChevronRight,
  TrendingUp,
  Wallet,
  FileWarning,
  PiggyBank,
  Plus,
  Minus,
  Calculator,
  CalendarDays,
  Briefcase,
  Timer,
  Info,
  TriangleAlert,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  Lightbulb,
  Repeat,
  Bell,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
  Line,
  ComposedChart,
  Area,
} from "recharts";
import {
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfWeek,
  endOfWeek,
  addDays,
  format,
  isWithinInterval,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  getDay,
} from "date-fns";
import { de } from "date-fns/locale";
import OnboardingModal from "@/components/OnboardingModal";
import UpgradePrompt from "@/components/UpgradePrompt";
import UpgradeRequiredView from "@/components/UpgradeRequiredView";
import { LayoutDashboard as LayoutDashboardIcon } from "lucide-react";
import { useIsPro } from "@/hooks/useIsPro";
import MobileScanPromo from "@/components/MobileScanPromo";
import SBRequestsWidget from "@/components/dashboard/SBRequestsWidget";
import QuoteHitRateWidget from "@/components/dashboard/QuoteHitRateWidget";
import { KPICard } from "@/components/ui/kpi-card";

const fmt = (value) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(value || 0);

const fmtCompact = (value) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const fmtHours = (h) =>
  new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(h || 0);

const PERIOD_OPTIONS = [
  { key: "month", label: "Monat" },
  { key: "quarter", label: "Quartal" },
  { key: "year", label: "Jahr" },
];

function getPeriodRange(periodKey, selectedYear) {
  const now = new Date();
  const isCurrentYear = selectedYear === now.getFullYear();
  const baseDate = isCurrentYear ? now : new Date(selectedYear, 11, 31);
  switch (periodKey) {
    case "month":
      return { start: startOfMonth(baseDate), end: endOfMonth(baseDate) };
    case "quarter":
      return { start: startOfQuarter(baseDate), end: endOfQuarter(baseDate) };
    case "year":
      return { start: new Date(selectedYear, 0, 1), end: new Date(selectedYear, 11, 31, 23, 59, 59) };
    default:
      return { start: startOfMonth(baseDate), end: endOfMonth(baseDate) };
  }
}

function getPrevPeriodRange(periodKey, selectedYear) {
  const now = new Date();
  const isCurrentYear = selectedYear === now.getFullYear();
  const baseDate = isCurrentYear ? now : new Date(selectedYear, 11, 31);
  switch (periodKey) {
    case "month": {
      const prev = subMonths(baseDate, 1);
      return { start: startOfMonth(prev), end: endOfMonth(prev) };
    }
    case "quarter": {
      const prev = subMonths(baseDate, 3);
      return { start: startOfQuarter(prev), end: endOfQuarter(prev) };
    }
    case "year": {
      const prevYear = new Date(selectedYear - 1, 0, 1);
      return { start: startOfYear(prevYear), end: endOfYear(prevYear) };
    }
    default: {
      const prev = subMonths(baseDate, 1);
      return { start: startOfMonth(prev), end: endOfMonth(prev) };
    }
  }
}

function trendPercent(current, previous) {
  if (current === 0 && previous === 0) return 0;
  if (!previous || previous === 0) return current > 0 ? 100 : -100;
  return Math.round(((current - previous) / Math.abs(previous)) * 100);
}

function TrendBadge({ current, previous, invertColor = false }) {
  const pct = trendPercent(current, previous);
  if (pct === 0) return null;
  const isUp = pct >= 0;
  const colorUp = invertColor ? "text-rose-700 bg-rose-50" : "text-emerald-700 bg-emerald-50";
  const colorDown = invertColor ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50";
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-medium ${isUp ? colorUp : colorDown}`}
      data-testid="trend-badge"
    >
      {isUp ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {Math.abs(pct)}%
    </span>
  );
}

function ActionItem({ icon: Icon, color, label, detail, href, testId }) {
  return (
    <a
      href={href}
      className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group"
      data-testid={testId}
    >
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${color}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 truncate">{label}</p>
        <p className="text-xs text-slate-500">{detail}</p>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
    </a>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-medium text-slate-900 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-slate-600">{entry.name}:</span>
          <span className="font-mono font-medium text-slate-900">{fmt(entry.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  // Iter 73j: useIsPro berücksichtigt has_lifetime_access (Lifetime-User mit plan='free' werden korrekt erkannt)
  const isProOrPaid = useIsPro() || (user?.plan && user.plan !== 'free');
  const hasAccess = isProOrPaid || !!user?.has_lifetime_access;

  // Free plan: show upgrade prompt (Iter 73i: konsistente UpgradeRequiredView)
  if (!hasAccess) {
    return (
      <UpgradeRequiredView
        source="dashboard_main"
        icon={LayoutDashboardIcon}
        title="Dashboard-Übersicht"
        subtitle="Alle Finanzen, Kunden und Projekte auf einen Blick — ab Basic verfügbar."
        features={[
          'Umsatz-, Ausgaben- und Gewinn-KPIs in Echtzeit',
          'Quartalsweise Trendanalyse',
          'Offene Rechnungen und überfällige Posten',
          'Top-Kunden und Top-Projekte',
          'Direkter Zugriff auf alle Module',
        ]}
      />
    );
  }

  return <DashboardContent />;
}

function DashboardContent() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("year");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState({
    invoices: [],
    expenses: [],
    timeEntries: [],
    projects: [],
    customers: [],
    receiptStats: null,
    incomes: [],
  });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [forecastData, setForecastData] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastScenario, setForecastScenario] = useState("realistic");
  const [timeStats, setTimeStats] = useState(null);
  const [timeStatsLoading, setTimeStatsLoading] = useState(false);
  const [taxData, setTaxData] = useState(null);
  // Sep 2026 — unbestätigte Belege (E-Mail/Scan/Datum außerhalb Jahr) für Banner + Hinweis
  const [pendingReceipts, setPendingReceipts] = useState(null);
  useEffect(() => { importFreeTimerEntries(); }, []);

  useEffect(() => {
    api.get("/inbound/pending-count").then((res) => setPendingReceipts(res.data || null)).catch(() => {});
  }, []);
  const [taxLoading, setTaxLoading] = useState(false);
  const [manualRevenue, setManualRevenue] = useState("");
  const [manualExpenses, setManualExpenses] = useState("");
  const manualDebounceRef = useRef(null);
  const [recurring, setRecurring] = useState(null);  // {mrr_net, active_count}
  const [dunning, setDunning] = useState(null);  // {total_open, dunnable_count, items}

  const isPro = useIsPro();
  const navigate = useNavigate();
  const hasLifetime = !!user?.has_lifetime_access;
  const currentYear = new Date().getFullYear();
  const isCurrentYear = selectedYear === currentYear;

  useEffect(() => {
    const flag = localStorage.getItem("show_onboarding");
    if (flag === "true") {
      setShowOnboarding(true);
      localStorage.removeItem("show_onboarding");
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [invoices, expenses, timeEntries, projects, customers, receiptStats, incomes] =
        await Promise.all([
          api.get("/invoices"),
          api.get("/expenses"),
          api.get("/time-entries"),
          api.get("/projects"),
          api.get("/customers"),
          api.get("/expenses/receipt-stats").catch(() => ({ data: null })),
          api.get("/incomes").catch(() => ({ data: [] })),
        ]);
      setRawData({
        invoices: invoices.data || [],
        expenses: expenses.data || [],
        timeEntries: timeEntries.data || [],
        projects: projects.data || [],
        customers: customers.data || [],
        receiptStats: receiptStats.data,
        incomes: incomes.data || [],
      });
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Juni 2026 — MRR aus wiederkehrenden Rechnungen (Pro-only, 403 für Free/Basic → still ignorieren)
  useEffect(() => {
    if (!isPro) return;
    let active = true;
    api.get("/recurring-invoices")
      .then((res) => { if (active) setRecurring({ mrr_net: res.data?.mrr_net || 0, active_count: res.data?.active_count || 0, billing_count: res.data?.billing_count || 0 }); })
      .catch(() => {});
    return () => { active = false; };
  }, [isPro]);

  // Juni 2026 — Offene/mahnbare Forderungen (für alle Plan-Stufen mit Dashboard-Zugriff)
  useEffect(() => {
    let active = true;
    api.get("/invoices/dunning/overview")
      .then((res) => { if (active) setDunning({
        total_open: res.data?.total_open || 0,
        dunnable_count: res.data?.dunnable_count || 0,
        open_count: (res.data?.items || []).length,
      }); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // Fetch forecast data
  const fetchForecast = useCallback(async (scenario, manRev = "", manExp = "") => {
    if (!isCurrentYear) { setForecastData(null); return; }
    setForecastLoading(true);
    try {
      // Always request 12 months so December is guaranteed regardless of forecast start month
      const remainingMonths = 12;
      let url = `/dashboard/forecast?months_ahead=${remainingMonths}&scenario=${scenario}`;
      if (manRev !== "" && !isNaN(parseFloat(manRev))) url += `&manual_monthly_revenue=${parseFloat(manRev)}`;
      if (manExp !== "" && !isNaN(parseFloat(manExp))) url += `&manual_monthly_expenses=${parseFloat(manExp)}`;
      const res = await api.get(url);
      setForecastData(res.data);
    } catch (err) {
      console.error("Forecast fetch error:", err);
      setForecastData(null);
    } finally {
      setForecastLoading(false);
    }
  }, [isCurrentYear]);

  useEffect(() => {
    clearTimeout(manualDebounceRef.current);
    const delay = (manualRevenue !== "" || manualExpenses !== "") ? 600 : 0;
    manualDebounceRef.current = setTimeout(() => {
      fetchForecast(forecastScenario, manualRevenue, manualExpenses);
    }, delay);
    return () => clearTimeout(manualDebounceRef.current);
  }, [forecastScenario, manualRevenue, manualExpenses, fetchForecast]);

  // Fetch time-entries stats
  const fetchTimeStats = useCallback(async () => {
    setTimeStatsLoading(true);
    try {
      const range = getPeriodRange(period, selectedYear);
      const dateFrom = format(range.start, "yyyy-MM-dd");
      const dateTo = format(range.end, "yyyy-MM-dd");
      const res = await api.get(`/dashboard/time-entries-stats?date_from=${dateFrom}&date_to=${dateTo}`);
      setTimeStats(res.data);
    } catch (err) {
      console.error("Time stats fetch error:", err);
      setTimeStats(null);
    } finally {
      setTimeStatsLoading(false);
    }
  }, [period, selectedYear]);

  useEffect(() => {
    fetchTimeStats();
  }, [fetchTimeStats]);

  // Fetch tax data for the selected year
  const fetchTaxData = useCallback(async () => {
    if (!isPro) { setTaxData(null); return; }
    setTaxLoading(true);
    try {
      const res = await api.get(`/tax/calculate?year=${selectedYear}`);
      setTaxData(res.data);
    } catch (err) {
      console.error("Tax fetch error:", err);
      setTaxData(null);
    } finally {
      setTaxLoading(false);
    }
  }, [selectedYear, isPro]);

  useEffect(() => {
    fetchTaxData();
  }, [fetchTaxData]);

  const metrics = useMemo(() => {
    const { invoices, expenses, timeEntries, projects, incomes = [] } = rawData;
    const range = getPeriodRange(period, selectedYear);
    const prevRange = getPrevPeriodRange(period, selectedYear);

    const inRange = (dateStr, r) => {
      if (!dateStr) return false;
      const d = new Date(dateStr);
      return isWithinInterval(d, { start: r.start, end: r.end });
    };

    // Einnahmen ohne ivoliq-Rechnung (importierte externe Rechnungen + manuelle Einnahmen).
    // Aus ivoliq-Rechnungen erzeugte Einnahmen (invoice_id gesetzt) NICHT mitzählen —
    // diese sind bereits über die bezahlten Rechnungen abgedeckt (kein Doppelzählen).
    const externalIncome = incomes.filter((i) => !i.invoice_id);

    // Revenue (paid invoices — Brutto to match Finanzen tab) + externe/importierte Einnahmen
    const periodRevenue = invoices
      .filter((i) => i.status === "paid" && inRange(i.invoice_date, range))
      .reduce((s, i) => s + (i.total || i.total_amount || i.subtotal || 0), 0)
      + externalIncome
        .filter((i) => inRange(i.date || i.invoice_date, range))
        .reduce((s, i) => s + (i.amount || 0), 0);
    const prevRevenue = invoices
      .filter((i) => i.status === "paid" && inRange(i.invoice_date, prevRange))
      .reduce((s, i) => s + (i.total || i.total_amount || i.subtotal || 0), 0)
      + externalIncome
        .filter((i) => inRange(i.date || i.invoice_date, prevRange))
        .reduce((s, i) => s + (i.amount || 0), 0);

    // Expenses
    const periodExpenses = expenses
      .filter((e) => inRange(e.date, range))
      .reduce((s, e) => s + (e.amount || 0), 0);
    const prevExpenses = expenses
      .filter((e) => inRange(e.date, prevRange))
      .reduce((s, e) => s + (e.amount || 0), 0);

    // Profit
    const periodProfit = periodRevenue - periodExpenses;
    const prevProfit = prevRevenue - prevExpenses;

    // Open invoices (created or sent, not paid/cancelled)
    const openInvoices = invoices.filter(
      (i) => i.status === "created" || i.status === "sent"
    );
    const openAmount = openInvoices.reduce(
      (s, i) => s + (i.total_amount || i.subtotal || 0),
      0
    );

    // Overdue invoices
    const now = new Date();
    const overdueInvoices = invoices.filter((i) => {
      if (i.status !== "sent" && i.status !== "created") return false;
      const due = i.due_date ? new Date(i.due_date) : null;
      return due && due < now;
    });
    const overdueAmount = overdueInvoices.reduce(
      (s, i) => s + (i.total_amount || i.subtotal || 0),
      0
    );

    // Unbilled hours
    const unbilledEntries = timeEntries.filter((e) => !e.invoiced);
    const unbilledHours =
      unbilledEntries.reduce(
        (s, e) => s + (e.net_duration_minutes || e.duration_minutes || 0),
        0
      ) / 60;

    // Missing receipts
    const missingReceipts = rawData.receiptStats?.missing_receipt || 0;

    // Tax reserve (rough estimate: 30% of profit)
    const taxReserve = Math.max(0, periodProfit * 0.3);

    // Monthly chart data (last 6 months OR full year if year view)
    const chartData = [];
    if (period === "year") {
      // Show all 12 months of the selected year
      for (let m = 0; m < 12; m++) {
        const monthDate = new Date(selectedYear, m, 1);
        const mStart = startOfMonth(monthDate);
        const mEnd = endOfMonth(monthDate);
        const mRange = { start: mStart, end: mEnd };
        const rev = invoices
          .filter((inv) => inv.status === "paid" && inRange(inv.invoice_date, mRange))
          .reduce((s, inv) => s + (inv.subtotal || inv.total_amount || 0), 0)
          + externalIncome
            .filter((i) => inRange(i.date || i.invoice_date, mRange))
            .reduce((s, i) => s + (i.amount || 0), 0);
        const exp = expenses
          .filter((e) => inRange(e.date, mRange))
          .reduce((s, e) => s + (e.amount || 0), 0);
        chartData.push({
          month: format(monthDate, "MMM", { locale: de }),
          Einnahmen: rev,
          Ausgaben: exp,
        });
      }
    } else {
      for (let i = 5; i >= 0; i--) {
        const m = subMonths(isCurrentYear ? now : new Date(selectedYear, 11, 31), i);
        const mStart = startOfMonth(m);
        const mEnd = endOfMonth(m);
        const mRange = { start: mStart, end: mEnd };
        const rev = invoices
          .filter((inv) => inv.status === "paid" && inRange(inv.invoice_date, mRange))
          .reduce((s, inv) => s + (inv.subtotal || inv.total_amount || 0), 0)
          + externalIncome
            .filter((i) => inRange(i.date || i.invoice_date, mRange))
            .reduce((s, i) => s + (i.amount || 0), 0);
        const exp = expenses
          .filter((e) => inRange(e.date, mRange))
          .reduce((s, e) => s + (e.amount || 0), 0);
        chartData.push({
          month: format(m, "MMM yy", { locale: de }),
          Einnahmen: rev,
          Ausgaben: exp,
        });
      }
    }

    // Top projects by hours
    const projectHours = {};
    timeEntries.forEach((e) => {
      if (!e.project_id) return;
      if (!projectHours[e.project_id]) projectHours[e.project_id] = 0;
      projectHours[e.project_id] += (e.net_duration_minutes || e.duration_minutes || 0) / 60;
    });
    const topProjects = projects
      .map((p) => ({
        ...p,
        trackedHours: projectHours[p.id] || 0,
        budgetPercent: p.budget_hours
          ? Math.round(((projectHours[p.id] || 0) / p.budget_hours) * 100)
          : null,
      }))
      .filter((p) => p.trackedHours > 0)
      .sort((a, b) => b.trackedHours - a.trackedHours)
      .slice(0, 5);

    // Weekly time summary (current week — only for current year)
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
    const weekDays = [];
    const dayNames = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
    if (isCurrentYear) {
      for (let d = 0; d < 7; d++) {
        const day = addDays(weekStart, d);
        const dayEntries = timeEntries.filter((e) => {
          const eDate = new Date(e.start_time);
          return (
            eDate.getFullYear() === day.getFullYear() &&
            eDate.getMonth() === day.getMonth() &&
            eDate.getDate() === day.getDate()
          );
        });
        const hours =
          dayEntries.reduce(
            (s, e) => s + (e.net_duration_minutes || e.duration_minutes || 0),
            0
          ) / 60;
        const unbilled =
          dayEntries
            .filter((e) => !e.invoiced)
            .reduce(
              (s, e) => s + (e.net_duration_minutes || e.duration_minutes || 0),
              0
            ) / 60;
        weekDays.push({
          day: dayNames[d],
          date: format(day, "dd.MM."),
          hours,
          unbilled,
          isToday: day.toDateString() === now.toDateString(),
        });
      }
    }

    const weekTotal = weekDays.reduce((s, d) => s + d.hours, 0);

    return {
      periodRevenue,
      prevRevenue,
      periodExpenses,
      prevExpenses,
      periodProfit,
      prevProfit,
      openInvoices,
      openAmount,
      overdueInvoices,
      overdueAmount,
      unbilledHours,
      missingReceipts,
      taxReserve,
      chartData,
      topProjects,
      weekDays,
      weekTotal,
    };
  }, [rawData, period, selectedYear, isCurrentYear]);

  // Action items
  const actionItems = useMemo(() => {
    const items = [];
    if (metrics.overdueInvoices.length > 0) {
      items.push({
        icon: AlertTriangle,
        color: "bg-rose-100 text-rose-700",
        label: `${metrics.overdueInvoices.length} Rechnung${metrics.overdueInvoices.length > 1 ? "en" : ""} überfällig`,
        detail: fmt(metrics.overdueAmount),
        href: "/invoices",
        testId: "action-overdue-invoices",
      });
    }
    if (metrics.unbilledHours > 0) {
      items.push({
        icon: Clock,
        color: "bg-amber-100 text-amber-700",
        label: `${fmtHours(metrics.unbilledHours)}h offene Zeiten`,
        detail: "Noch nicht abgerechnet",
        href: "/time-tracking",
        testId: "action-unbilled-hours",
      });
    }
    if (metrics.missingReceipts > 0) {
      items.push({
        icon: FileWarning,
        color: "bg-orange-100 text-orange-700",
        label: `${metrics.missingReceipts} Beleg${metrics.missingReceipts > 1 ? "e" : ""} fehlen`,
        detail: "Für Steuerberater hochladen",
        href: "/finances",
        testId: "action-missing-receipts",
      });
    }
    if (metrics.openInvoices.length > 0 && metrics.overdueInvoices.length === 0) {
      items.push({
        icon: FileText,
        color: "bg-blue-100 text-blue-700",
        label: `${metrics.openInvoices.length} offene Rechnung${metrics.openInvoices.length > 1 ? "en" : ""}`,
        detail: fmt(metrics.openAmount),
        href: "/invoices",
        testId: "action-open-invoices",
      });
    }
    if (metrics.taxReserve > 0) {
      items.push({
        icon: PiggyBank,
        color: "bg-emerald-100 text-emerald-700",
        label: "Steuerrücklage",
        detail: `${fmt(metrics.taxReserve)} empfohlen`,
        href: "/taxes",
        testId: "action-tax-reserve",
      });
    }
    return items;
  }, [metrics]);

  // Smart tax-saving tips based on real user data
  const taxTips = useMemo(() => {
    if (!isCurrentYear) return [];
    const tips = [];
    const country = user?.country || "DE";
    const now = new Date();
    const currentMonth = now.getMonth(); // 0-indexed

    // 0. Sep 2026 — Belegdatum außerhalb des laufenden Jahres (KI-Lesefehler?) — falsche Steuerperiode
    if (pendingReceipts?.off_year_count > 0) {
      const n = pendingReceipts.off_year_count;
      tips.push({
        icon: AlertTriangle,
        color: "bg-amber-50 border-amber-200 text-amber-900",
        iconBg: "bg-amber-100 text-amber-600",
        title: "Belegdatum prüfen",
        text: `${n} gescannte${n === 1 ? "r" : ""} Beleg${n === 1 ? " hat" : "e haben"} ein Datum außerhalb ${now.getFullYear()}. Ein falsch erkanntes Jahr verschiebt die Ausgabe in eine andere Steuerperiode — bitte kontrollieren.`,
        action: { label: "Belege prüfen", href: "/finances?filter=inbox" },
        testId: "tip-offyear-receipts",
      });
    }

    // 1. Missing receipts → Vorsteuerabzug
    if (metrics.missingReceipts > 0) {
      const potentialVat = metrics.missingReceipts * 15; // rough avg
      tips.push({
        icon: Receipt,
        color: "bg-orange-50 border-orange-200 text-orange-900",
        iconBg: "bg-orange-100 text-orange-600",
        title: "Belege hochladen = Vorsteuer sichern",
        text: `${metrics.missingReceipts} Ausgaben ohne Beleg. Ohne Beleg kein Vorsteuerabzug bei einer Betriebsprüfung.`,
        action: { label: "Belege hochladen", href: "/finances" },
        testId: "tip-missing-receipts",
      });
    }

    // 2. Unbilled hours → Liquidität
    if (metrics.unbilledHours > 10) {
      tips.push({
        icon: Clock,
        color: "bg-amber-50 border-amber-200 text-amber-900",
        iconBg: "bg-amber-100 text-amber-600",
        title: "Offene Zeiten abrechnen",
        text: `${fmtHours(metrics.unbilledHours)}h noch nicht abgerechnet. Regelmäßige Abrechnung verbessert deinen Cashflow und vermeidet Jahresend-Stress.`,
        action: { label: "Zeiten prüfen", href: "/time-tracking" },
        testId: "tip-unbilled-hours",
      });
    }

    // 3. Tax reserve reminder
    if (taxData && taxData.total_tax_burden > 0 && taxData.taxable_income > 0) {
      const monthlyReserve = taxData.total_tax_burden / 12;
      tips.push({
        icon: PiggyBank,
        color: "bg-emerald-50 border-emerald-200 text-emerald-900",
        iconBg: "bg-emerald-100 text-emerald-600",
        title: "Steuerrücklage bilden",
        text: `Bei deinem aktuellen Gewinn solltest du ca. ${fmt(monthlyReserve)}/Monat für Steuern zurücklegen (Gesamt: ${fmt(taxData.total_tax_burden)}).`,
        testId: "tip-tax-reserve",
      });
    }

    // 4. Expense ratio check — low expenses might mean missed deductions
    if (taxData && taxData.total_income_netto > 5000 && taxData.total_expenses_netto > 0) {
      const expenseRatio = taxData.total_expenses_netto / taxData.total_income_netto;
      if (expenseRatio < 0.15) {
        tips.push({
          icon: Calculator,
          color: "bg-blue-50 border-blue-200 text-blue-900",
          iconBg: "bg-blue-100 text-blue-600",
          title: "Betriebsausgaben prüfen",
          text: `Deine Ausgabenquote liegt bei nur ${Math.round(expenseRatio * 100)}%. Prüfe, ob du alle Betriebsausgaben erfasst hast (Homeoffice, Internet, Telefon, Fachliteratur, Fortbildung).`,
          action: { label: "Ausgaben erfassen", href: "/finances?create=expense" },
          testId: "tip-low-expenses",
        });
      }
    }

    // 5. Kleinunternehmer-Grenze (DE: 22.000€, AT: 40.000€)
    if (taxData && taxData.total_income > 0) {
      const threshold = country === "DE" ? 22000 : country === "AT" ? 40000 : 100000;
      const label = country === "CH" ? "MWST-Pflichtgrenze" : "Kleinunternehmergrenze";
      const pct = (taxData.total_income / threshold) * 100;
      if (pct > 70 && pct < 100) {
        tips.push({
          icon: AlertTriangle,
          color: "bg-rose-50 border-rose-200 text-rose-900",
          iconBg: "bg-rose-100 text-rose-600",
          title: `${label} im Blick behalten`,
          text: `Du bist bei ${Math.round(pct)}% der ${label} (${fmtCompact(threshold)}). Bei Überschreitung wirst du umsatzsteuerpflichtig.`,
          action: { label: "Steuern ansehen", href: "/taxes" },
          testId: "tip-threshold-warning",
        });
      }
    }

    // 6. Year-end planning (Q4)
    if (currentMonth >= 9 && taxData && taxData.taxable_income > 0) {
      if (country === "AT") {
        tips.push({
          icon: CalendarDays,
          color: "bg-violet-50 border-violet-200 text-violet-900",
          iconBg: "bg-violet-100 text-violet-600",
          title: "Gewinnfreibetrag nutzen",
          text: "Vor Jahresende: Investiere in begünstigte Anlagegüter oder Wertpapiere, um den investitionsbedingten Gewinnfreibetrag zu nutzen.",
          action: { label: "Steuern ansehen", href: "/taxes" },
          testId: "tip-at-investment",
        });
      } else {
        tips.push({
          icon: CalendarDays,
          color: "bg-violet-50 border-violet-200 text-violet-900",
          iconBg: "bg-violet-100 text-violet-600",
          title: "Jahresende-Planung",
          text: "Tipp: Plane größere Anschaffungen noch vor Jahresende, um deine Steuerlast für dieses Jahr zu senken.",
          testId: "tip-year-end",
        });
      }
    }

    // 7. No tax data yet (new user / no income)
    if (taxData && taxData.total_income === 0 && taxData.total_expenses === 0 && tips.length === 0) {
      tips.push({
        icon: TrendingUp,
        color: "bg-slate-50 border-slate-200 text-slate-700",
        iconBg: "bg-slate-100 text-slate-500",
        title: "Loslegen",
        text: "Erfasse deine erste Rechnung oder Ausgabe, um personalisierte Steuer-Tipps zu erhalten.",
        action: { label: "Rechnung erstellen", href: "/invoices?create=1" },
        testId: "tip-get-started",
      });
    }

    return tips.slice(0, 3); // max 3 tips to keep it clean
  }, [metrics, taxData, user, isCurrentYear, pendingReceipts]);

  // Combined chart data: actual months (solid) + forecast months (dashed)
  // Transition = always current month. Dashed line starts at the SAME value as solid ends → smooth visual handoff.
  const combinedForecastChartData = useMemo(() => {
    if (!isCurrentYear) return [];
    const now = new Date();
    const currentMonthIdx = now.getMonth(); // 0-indexed
    const { invoices, expenses, incomes = [] } = rawData;

    const inR = (dateStr, r) => {
      if (!dateStr) return false;
      return isWithinInterval(new Date(dateStr), { start: r.start, end: r.end });
    };

    // Externe/importierte Einnahmen (ohne ivoliq-Rechnungsbezug) zählen zum Ist-Umsatz —
    // sonst zeigen Monate mit nur importierten Ausgangsrechnungen fälschlich 0.
    const externalIncome = incomes.filter((i) => !i.invoice_id && !i.deactivated_at);
    const incomeNet = (i) => {
      if (i.net_amount != null) return i.net_amount;
      const gross = i.amount || 0;
      const rate = parseFloat(i.vat_rate) || 0;
      return rate > 0 ? gross / (1 + rate / 100) : gross;
    };

    // Compute actual data for each month up to currentMonth (0 if no data — avoids line gaps)
    const monthlyActuals = Array.from({ length: 12 }, (_, m) => {
      if (m > currentMonthIdx) return { revenue: 0, expenses: 0 };
      const mDate = new Date(selectedYear, m, 1);
      const mRange = { start: startOfMonth(mDate), end: endOfMonth(mDate) };
      return {
        // NETTO-Werte für konsistente Darstellung mit dem Forecast (Backend liefert netto)
        revenue: invoices
          .filter(i => i.status === "paid" && inR(i.invoice_date, mRange))
          .reduce((s, i) => s + (i.subtotal ?? i.total ?? i.total_amount ?? 0), 0)
          + externalIncome
            .filter(i => inR(i.date || i.invoice_date, mRange))
            .reduce((s, i) => s + incomeNet(i), 0),
        expenses: expenses
          .filter(e => inR(e.date, mRange))
          .reduce((s, e) => {
            const gross = e.amount || 0;
            const rate = parseFloat(e.vat_rate) || 0;
            return s + (rate > 0 ? gross / (1 + rate / 100) : gross);
          }, 0),
      };
    });

    // Build forecast lookup by YYYY-MM
    const forecastByMonth = {};
    forecastData?.forecast?.forEach(item => { forecastByMonth[item.month] = item; });

    const manRev = manualRevenue !== "" ? parseFloat(manualRevenue) || 0 : null;
    const manExp = manualExpenses !== "" ? parseFloat(manualExpenses) || 0 : null;
    const monthNames = ["Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"];

    return Array.from({ length: 12 }, (_, m) => {
      const monthLabel = monthNames[m];
      const yearMonthStr = `${selectedYear}-${String(m + 1).padStart(2, "0")}`;
      const fData = forecastByMonth[yearMonthStr];
      const forecastRev = manRev !== null ? manRev : (fData?.revenue ?? 0);
      const forecastExp = manExp !== null ? manExp : (fData?.expenses ?? 0);
      const actual = monthlyActuals[m];

      if (m < currentMonthIdx) {
        // Past months: solid only, use 0 (not null) → no line gaps
        return { month: monthLabel, actual_revenue: actual.revenue, actual_expenses: actual.expenses, forecast_revenue: null, forecast_expenses: null };
      } else if (m === currentMonthIdx) {
        // Current month "blend": solid = actual so far, dashed = full forecast for this month
        // The visual gap between them shows exactly how much is still expected this month
        return { month: monthLabel, actual_revenue: actual.revenue, actual_expenses: actual.expenses, forecast_revenue: forecastRev, forecast_expenses: forecastExp };
      } else {
        // Future months: dashed forecast only
        return { month: monthLabel, actual_revenue: null, actual_expenses: null, forecast_revenue: forecastRev, forecast_expenses: forecastExp };
      }
    });
  }, [rawData, forecastData, selectedYear, isCurrentYear, manualRevenue, manualExpenses]);

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Guten Morgen";
    if (hour < 18) return "Guten Tag";
    return "Guten Abend";
  }, []);

  const periodLabel = useMemo(() => {
    const range = getPeriodRange(period, selectedYear);
    if (period === "month") return format(range.start, "MMMM yyyy", { locale: de });
    if (period === "quarter") {
      const q = Math.ceil((range.start.getMonth() + 1) / 3);
      return `Q${q} ${range.start.getFullYear()}`;
    }
    return selectedYear.toString();
  }, [period, selectedYear]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="dashboard-loading">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-emerald-600" />
          <p className="text-sm text-slate-500">Dashboard wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <OnboardingModal
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        userName={user?.name}
      />

      <div className="space-y-6 sm:space-y-8" data-testid="dashboard">
        {/* Juli 2026 — Hinweis auf unbestätigte E-Mail-Belege */}
        <InboundPendingBanner pending={pendingReceipts} />

        {/* Row 0: Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              {greeting}{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {periodLabel} &middot; Deine Finanzübersicht
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Year Selector */}
            <div className="inline-flex items-center gap-1 bg-slate-100 p-1 rounded-lg" data-testid="year-selector">
              <button
                onClick={() => setSelectedYear((y) => y - 1)}
                className="p-1.5 rounded-md hover:bg-white hover:shadow-sm transition-all text-slate-500 hover:text-slate-700"
                data-testid="year-prev-btn"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <span className="px-2 py-1 text-xs font-semibold text-slate-900 min-w-[3rem] text-center font-mono" data-testid="year-display">
                {selectedYear}
              </span>
              <button
                onClick={() => setSelectedYear((y) => Math.min(y + 1, currentYear))}
                disabled={selectedYear >= currentYear}
                className="p-1.5 rounded-md hover:bg-white hover:shadow-sm transition-all text-slate-500 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="year-next-btn"
              >
                <ChevronRightIcon className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Period filter */}
            <div className="inline-flex bg-slate-100 p-1 rounded-lg" data-testid="period-filter">
              {PERIOD_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setPeriod(opt.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    period === opt.key
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid={`filter-${opt.key}-btn`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="hidden sm:flex items-center gap-1.5">
              <Button size="sm" variant="outline" className="gap-1.5 text-xs h-8" asChild data-testid="qa-invoice">
                <a href="/invoices?create=1">
                  <FileText className="h-3.5 w-3.5" />
                  Rechnung
                </a>
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5 text-xs h-8" asChild data-testid="qa-expense">
                <a href="/finances?create=expense">
                  <Receipt className="h-3.5 w-3.5" />
                  Ausgabe
                </a>
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5 text-xs h-8" asChild data-testid="qa-time">
                <a href="/time-tracking?create=1">
                  <Clock className="h-3.5 w-3.5" />
                  Zeit
                </a>
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile-Scan-Promo (Iter 71 Feedback) */}
        <MobileScanPromo />

        {/* Year Indicator Banner (for past years) */}
        {!isCurrentYear && (
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-200" data-testid="past-year-banner">
            <CalendarDays className="h-4 w-4 text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-800">
              Du siehst Daten aus <span className="font-semibold">{selectedYear}</span>. Forecast und aktuelle Woche sind nur für das laufende Jahr verfügbar.
            </p>
            <button
              onClick={() => setSelectedYear(currentYear)}
              className="ml-auto text-xs font-medium text-amber-700 hover:text-amber-900 underline flex-shrink-0"
            >
              Zum aktuellen Jahr
            </button>
          </div>
        )}

        {/* Feb 2026 — SB-Anfragen-Widget (nur sichtbar wenn offene Anfragen) */}
        <SBRequestsWidget />

        {/* Row 1: Hero KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6">
          <div className="md:col-span-4">
            <KPICard
              title="Umsatz"
              value={fmt(metrics.periodRevenue)}
              subtitle={`vs. Vorperiode: ${fmt(metrics.prevRevenue)}`}
              trend={<TrendBadge current={metrics.periodRevenue} previous={metrics.prevRevenue} />}
              icon={TrendingUp}
              iconColor="bg-emerald-50 text-emerald-600"
              testId="kpi-revenue-card"
            />
          </div>
          <div className="md:col-span-4">
            <KPICard
              title="Gewinn"
              value={fmt(metrics.periodProfit)}
              subtitle={`Steuerrücklage: ~${fmtCompact(metrics.taxReserve)}`}
              trend={<TrendBadge current={metrics.periodProfit} previous={metrics.prevProfit} />}
              icon={Wallet}
              iconColor={metrics.periodProfit >= 0 ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}
              testId="kpi-profit-card"
            />
          </div>
          <div className="md:col-span-4">
            <KPICard
              title="Offene Rechnungen"
              value={fmt(metrics.openAmount)}
              subtitle={`${metrics.openInvoices.length} Rechnung${metrics.openInvoices.length !== 1 ? "en" : ""}${metrics.overdueInvoices.length > 0 ? ` · ${metrics.overdueInvoices.length} überfällig` : ""}`}
              trend={
                metrics.overdueInvoices.length > 0 ? (
                  <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-medium bg-rose-50 text-rose-700">
                    <AlertTriangle className="h-3 w-3" />
                    Überfällig
                  </span>
                ) : null
              }
              icon={FileText}
              iconColor={metrics.overdueInvoices.length > 0 ? "bg-rose-50 text-rose-600" : "bg-blue-50 text-blue-600"}
              testId="kpi-open-invoices-card"
            />
          </div>
        </div>

        {/* Juni 2026 — Offene Forderungen / mahnbar (Cashflow-Hebel → 1 Klick ins Mahnwesen) */}
        {dunning && dunning.dunnable_count > 0 && (
          <button
            type="button"
            onClick={() => navigate("/invoices")}
            data-testid="dashboard-dunning-widget"
            className="w-full flex items-center justify-between gap-4 rounded-2xl border border-orange-200 bg-gradient-to-r from-orange-50 to-white px-5 py-4 text-left transition-colors hover:border-orange-300"
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-orange-100 text-orange-700">
                <Bell className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-orange-700">Offene Forderungen · mahnbar</p>
                <p className="text-sm text-slate-500">
                  {dunning.dunnable_count} {dunning.dunnable_count === 1 ? "Rechnung" : "Rechnungen"} überfällig und bereit zum Mahnen
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-semibold font-mono tracking-tight text-orange-800">{fmt(dunning.total_open)}</p>
              <p className="text-xs text-slate-400">offen · jetzt mahnen →</p>
            </div>
          </button>
        )}

        {/* Juni 2026 — MRR-Widget (wiederkehrende Rechnungen) */}
        {recurring && recurring.active_count > 0 && (
          <button
            type="button"
            onClick={() => navigate("/recurring")}
            data-testid="dashboard-mrr-widget"
            className="w-full flex items-center justify-between gap-4 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white px-5 py-4 text-left transition-colors hover:border-emerald-300"
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-emerald-100 text-emerald-700">
                <Repeat className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-emerald-700">Wiederkehrender Umsatz / Monat</p>
                <p className="text-sm text-slate-500">
                  {(recurring.billing_count ?? 0)} von {recurring.active_count} {recurring.active_count === 1 ? "Dauerauftrag" : "Daueraufträgen"} im {new Date().toLocaleString("de-DE", { month: "long" })} fällig
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-semibold font-mono tracking-tight text-emerald-800">{fmt(recurring.mrr_net)}</p>
              <p className="text-xs text-slate-400">netto · {new Date().toLocaleString("de-DE", { month: "long" })}</p>
            </div>
          </button>
        )}

        {/* Juni 2026 — Angebote-Hit-Rate-Widget */}
        <QuoteHitRateWidget year={selectedYear} />

        {/* Row 2: Action Items + Revenue Chart */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6">
          {/* Action Items */}
          <div className="md:col-span-5 lg:col-span-4">
            <Card className="h-full border-l-4 border-l-amber-400" data-testid="action-items-card">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                    Handlungsbedarf
                  </h3>
                  {actionItems.length > 0 && (
                    <Badge variant="secondary" className="text-xs ml-auto">
                      {actionItems.length}
                    </Badge>
                  )}
                </div>
                {actionItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
                      <TrendingUp className="h-5 w-5 text-emerald-600" />
                    </div>
                    <p className="text-sm font-medium text-slate-900">Alles erledigt</p>
                    <p className="text-xs text-slate-500 mt-1">Keine offenen Punkte</p>
                  </div>
                ) : (
                  <div className="space-y-1" data-testid="action-items-list">
                    {actionItems.map((item, i) => (
                      <ActionItem key={i} {...item} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Revenue vs Expenses Chart */}
          <div className="md:col-span-7 lg:col-span-8">
            <Card className="h-full" data-testid="revenue-chart-card">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                    Umsatz vs. Ausgaben
                  </h3>
                  <span className="text-xs text-slate-500">
                    {period === "year" ? selectedYear : "Letzte 6 Monate"}
                  </span>
                </div>
                <div className="h-[220px] sm:h-[260px]" data-testid="revenue-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={metrics.chartData} barGap={4} barCategoryGap="20%">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: "#94a3b8" }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: "#94a3b8" }}
                        tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                        width={45}
                      />
                      <RechartsTooltip content={<ChartTooltip />} />
                      <Bar dataKey="Einnahmen" fill="#10b981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Ausgaben" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />
                    Einnahmen
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-rose-500" />
                    Ausgaben
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Row 3: Forecast (only current year + pro) */}
        {isPro && isCurrentYear && (
          <Card data-testid="forecast-card">
            <CardContent className="p-5 sm:p-6">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                <div>
                  <div className="flex items-center gap-2">
                    <Calculator className="h-4 w-4 text-slate-600" />
                    <h3 className="text-base font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                      Forecast {selectedYear}
                    </h3>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-0.5" data-testid="forecast-netto-note">Alle Beträge netto (exkl. USt)</p>
                </div>
                <div className="inline-flex bg-slate-100 p-1 rounded-lg border border-slate-200" data-testid="forecast-scenario-toggle">
                  {[
                    { key: "pessimistic", label: "Konservativ" },
                    { key: "realistic", label: "Neutral" },
                    { key: "optimistic", label: "Offensiv" },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setForecastScenario(opt.key)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                        forecastScenario === opt.key
                          ? "bg-white text-slate-900 shadow-sm"
                          : "text-slate-500 hover:text-slate-700"
                      }`}
                      data-testid={`forecast-${opt.key}-btn`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Manual monthly input overrides */}
              <div className="flex flex-col sm:flex-row gap-2 mb-5" data-testid="forecast-manual-inputs">
                <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200 flex-1">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 whitespace-nowrap flex-shrink-0">Ø Einnahmen/Monat (netto)</span>
                  <input
                    type="number"
                    value={manualRevenue}
                    onChange={e => setManualRevenue(e.target.value)}
                    placeholder="Auto"
                    min="0"
                    className="flex-1 min-w-0 text-sm font-mono font-medium text-slate-900 bg-transparent outline-none placeholder-slate-300 text-right"
                    data-testid="forecast-manual-revenue-input"
                  />
                  <span className="text-xs text-slate-400 flex-shrink-0">€</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200 flex-1">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 whitespace-nowrap flex-shrink-0">Ø Ausgaben/Monat (netto)</span>
                  <input
                    type="number"
                    value={manualExpenses}
                    onChange={e => setManualExpenses(e.target.value)}
                    placeholder="Auto"
                    min="0"
                    className="flex-1 min-w-0 text-sm font-mono font-medium text-slate-900 bg-transparent outline-none placeholder-slate-300 text-right"
                    data-testid="forecast-manual-expenses-input"
                  />
                  <span className="text-xs text-slate-400 flex-shrink-0">€</span>
                </div>
              </div>

              {forecastLoading || taxLoading ? (
                <div className="flex items-center justify-center py-10">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-emerald-600" />
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Konsolidierte Jahresprognose: Ist YTD + Prognose Rest = Gesamtjahr */}
                  <div className="relative flex flex-col gap-4 p-5 rounded-xl border border-slate-200 bg-white" data-testid="forecast-year-section">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="text-xs font-semibold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Jahresprognose {selectedYear}</span>
                        <p className="text-[10px] text-slate-400 mt-0.5">1. Jan – 31. Dez {selectedYear} · Ist bis heute + Prognose bis Jahresende</p>
                      </div>
                      <span className="flex-shrink-0 text-[10px] font-medium text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
                        Ist + Prognose
                      </span>
                    </div>
                    {forecastData ? (
                      <>
                        {(() => {
                          const yb = forecastData.year_breakdown || {};
                          const annualRev = yb.annual_revenue_net ?? 0;
                          const annualExp = yb.annual_expenses_net ?? 0;
                          const annualProfit = yb.annual_profit_net ?? (annualRev - annualExp);
                          return (
                            <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Umsatz (Netto)</p>
                                <p className="text-lg font-semibold font-mono text-slate-900" data-testid="forecast-year-revenue-value">
                                  {fmt(annualRev)}
                                </p>
                                <p className="text-[10px] text-slate-400 mt-1">
                                  Ist {fmt(yb.ytd_revenue_net ?? 0)} + Prognose {fmt(yb.forecast_revenue_net ?? 0)}
                                </p>
                              </div>
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Kosten (Netto)</p>
                                <p className="text-lg font-semibold font-mono text-rose-700">
                                  {fmt(annualExp)}
                                </p>
                                <p className="text-[10px] text-slate-400 mt-1">
                                  Ist {fmt(yb.ytd_expenses_net ?? 0)} + Prognose {fmt(yb.forecast_expenses_net ?? 0)}
                                </p>
                              </div>
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Gewinn (Netto)</p>
                                <p className={`text-lg font-semibold font-mono ${annualProfit >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="forecast-year-profit-value">
                                  {fmt(annualProfit)}
                                </p>
                                <p className="text-[10px] text-slate-400 mt-1">
                                  Ist {fmt(yb.ytd_profit_net ?? 0)} + Prognose {fmt(yb.forecast_profit_net ?? 0)}
                                </p>
                              </div>
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Steuerlast (auf Gewinn)</p>
                                <p className="text-lg font-semibold font-mono text-amber-700" data-testid="forecast-tax-total-value">
                                  {fmt(forecastData.tax_estimate?.total)}
                                </p>
                                <p className="text-[10px] text-slate-400 mt-1">ESt + Sozialvers. · USt separat (durchlaufend)</p>
                              </div>
                            </div>
                          );
                        })()}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-3 border-t border-dashed border-slate-200" data-testid="forecast-tax-kpis">
                          {[
                            { label: "Einkommensteuer", value: forecastData.tax_estimate?.income_tax, color: "text-slate-600" },
                            { label: "Sozialversicherung", value: forecastData.tax_estimate?.social_security, color: "text-amber-500" },
                            { label: "USt-Zahllast (durchlaufend)", value: forecastData.tax_estimate?.vat, color: "text-blue-500" },
                            { label: "Monatl. Rücklage (ESt + SV)", value: forecastData.tax_estimate?.monthly_reserve, color: "text-emerald-500" },
                          ].map((item) => (
                            <div key={item.label}>
                              <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">{item.label}</p>
                              <p className={`text-sm font-medium font-mono ${item.color}`}>{fmt(item.value)}</p>
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] text-slate-400 -mt-1" data-testid="forecast-vat-note">
                          Hinweis: Die USt ist ein durchlaufender Posten (vom Kunden vereinnahmt, ans Finanzamt abgeführt) und zählt nicht zur Steuerlast auf deinen Gewinn.
                        </p>
                        <p className="text-[10px] text-slate-400" data-testid="forecast-reserve-note">
                          Monatl. Rücklage = 1/12 der prognostizierten Jahressteuerlast — nur ESt + Sozialversicherung, ohne USt. Für die USt zusätzlich zurücklegen: {fmt(forecastData.tax_estimate?.vat_monthly_reserve)}/Monat.
                        </p>
                        {forecastData.year_breakdown && (
                          <p className="text-[10px] text-slate-400" data-testid="forecast-tax-basis-note">
                            Basis: {forecastData.year_breakdown.ytd_months} Monat(e) Ist + lfd. Monat (Ist bis heute) + {Math.max(0, forecastData.year_breakdown.forecast_months_in_year - 1)} Monat(e) Prognose = Jahresgewinn {fmt(forecastData.year_breakdown.annual_profit_net)} (netto)
                          </p>
                        )}
                        {/* August 2026 — IFB-Regler (AT): Maximum aus der Jahresprognose, unabhängig vom Steuern-Tab */}
                        <InvestmentAllowanceSlider
                          maxAmount={forecastData.tax_estimate?.at_ifb_max}
                          selectedAmount={forecastData.tax_estimate?.at_ifb_selected}
                          savingsCurve={forecastData.tax_estimate?.at_ifb_savings_curve}
                          onChanged={() => fetchForecast(forecastScenario, manualRevenue, manualExpenses)}
                        />
                      </>
                    ) : (
                      <p className="text-xs text-slate-400 py-6 text-center">Keine Prognose-Daten verfügbar.</p>
                    )}
                  </div>

                  {/* Forecast Chart — full year: actual (solid) + forecast (dashed) */}
                  <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50" data-testid="forecast-chart-container">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-3 font-bold">Jahresverlauf {selectedYear} · alle Werte netto</p>
                    <div className="h-[200px] sm:h-[220px]" data-testid="forecast-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={combinedForecastChartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                          <XAxis
                            dataKey="month"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: "#94a3b8" }}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: "#94a3b8" }}
                            tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                            width={40}
                          />
                          <RechartsTooltip
                            content={({ active, payload, label }) => {
                              if (!active || !payload?.length) return null;
                              return (
                                <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs">
                                  <p className="font-medium text-slate-900 mb-1">{label}</p>
                                  {payload.filter(e => e.value != null).map((e, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: e.color }} />
                                      <span className="text-slate-600">{e.name}:</span>
                                      <span className="font-mono font-medium text-slate-900">{fmt(e.value)}</span>
                                    </div>
                                  ))}
                                </div>
                              );
                            }}
                          />
                          {/* Actual data — solid, filled */}
                          <Area type="monotone" dataKey="actual_revenue" name="Umsatz (Ist)" stroke="#10b981" strokeWidth={2.5} fill="#d1fae5" fillOpacity={0.5} connectNulls={false} dot={false} activeDot={{ r: 3 }} />
                          <Area type="monotone" dataKey="actual_expenses" name="Ausgaben (Ist)" stroke="#ef4444" strokeWidth={2} fill="#fee2e2" fillOpacity={0.4} connectNulls={false} dot={false} activeDot={{ r: 3 }} />
                          {/* Forecast data — dashed, lighter fill */}
                          <Area type="monotone" dataKey="forecast_revenue" name="Umsatz (Prognose)" stroke="#10b981" strokeWidth={2} strokeDasharray="6 3" fill="#d1fae5" fillOpacity={0.15} connectNulls={false} dot={false} />
                          <Area type="monotone" dataKey="forecast_expenses" name="Ausgaben (Prognose)" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="6 3" fill="#fee2e2" fillOpacity={0.08} connectNulls={false} dot={false} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <span className="w-5 h-0" style={{ display: "inline-block", borderTop: "2.5px solid #10b981" }} />
                        <span>Umsatz Ist</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-5 h-0" style={{ display: "inline-block", borderTop: "2px dashed #10b981" }} />
                        <span>Umsatz Prognose</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-5 h-0" style={{ display: "inline-block", borderTop: "2px solid #ef4444" }} />
                        <span>Ausgaben Ist</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-5 h-0" style={{ display: "inline-block", borderTop: "2px dashed #ef4444" }} />
                        <span>Ausgaben Prognose</span>
                      </div>
                    </div>
                  </div>

                  {/* Recommendations as pills */}
                  {forecastData?.recommendations?.length > 0 && (
                    <div className="flex flex-wrap gap-2" data-testid="forecast-recommendations">
                      {forecastData.recommendations.map((rec, i) => (
                        <div
                          key={i}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                            rec.type === "warning"
                              ? "bg-amber-50 text-amber-700 border border-amber-200"
                              : "bg-blue-50 text-blue-700 border border-blue-200"
                          }`}
                        >
                          {rec.type === "warning" ? (
                            <TriangleAlert className="h-3.5 w-3.5 flex-shrink-0" />
                          ) : (
                            <Info className="h-3.5 w-3.5 flex-shrink-0" />
                          )}
                          <span>{rec.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Row 3.5: Smart Tax Tips (only current year) */}
        {isCurrentYear && taxTips.length > 0 && (
          <div data-testid="tax-tips-section">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 rounded-lg bg-amber-100">
                <Lightbulb className="h-3.5 w-3.5 text-amber-600" />
              </div>
              <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                Steuer-Spar-Tipps
              </h3>
              <span className="text-[10px] text-slate-400 uppercase tracking-wider ml-1">Personalisiert</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {taxTips.map((tip) => {
                const TipIcon = tip.icon;
                return (
                  <div
                    key={tip.testId}
                    className={`rounded-xl border p-4 ${tip.color} transition-all hover:shadow-sm`}
                    data-testid={tip.testId}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${tip.iconBg}`}>
                        <TipIcon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold mb-1">{tip.title}</p>
                        <p className="text-xs leading-relaxed opacity-80">{tip.text}</p>
                        {tip.action && (
                          <a
                            href={tip.action.href}
                            className="inline-flex items-center gap-1 mt-2 text-xs font-medium hover:underline"
                          >
                            {tip.action.label}
                            <ChevronRight className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Row 4: Time Evaluation */}
        <Card data-testid="time-evaluation-card">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Timer className="h-4 w-4 text-slate-600" />
                <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Zeitauswertung
                </h3>
              </div>
              <a href="/time-tracking" className="text-xs text-blue-600 hover:underline" data-testid="time-eval-link">
                Zur Zeiterfassung
              </a>
            </div>

            {timeStatsLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-emerald-600" />
              </div>
            ) : timeStats ? (
              <div className="space-y-5">
                {/* Time KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="time-eval-kpis">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Gesamt</p>
                    <p className="text-lg font-semibold font-mono text-slate-900">{fmtHours(timeStats.total_hours)}h</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-wider text-emerald-600 mb-1">Abgerechnet</p>
                    <p className="text-lg font-semibold font-mono text-emerald-700">{fmtHours(timeStats.invoiced_hours)}h</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-wider text-amber-600 mb-1">Offen</p>
                    <p className="text-lg font-semibold font-mono text-amber-700">{fmtHours(timeStats.open_hours)}h</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">&Oslash; / Tag</p>
                    <p className="text-lg font-semibold font-mono text-slate-900">{fmtHours(timeStats.avg_hours_per_day)}h</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Einträge</p>
                    <p className="text-lg font-semibold font-mono text-slate-900">{timeStats.entry_count}</p>
                  </div>
                </div>

                {/* Project Breakdown + Daily Chart side by side */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  {/* Project Breakdown */}
                  <div className="md:col-span-5">
                    <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">Stunden nach Projekt</h4>
                    {timeStats.by_project?.length > 0 ? (
                      <div className="space-y-3" data-testid="time-project-breakdown">
                        {timeStats.by_project.slice(0, 6).map((p) => {
                          const totalH = p.total_minutes / 60;
                          const invoicedPct = p.total_minutes > 0 ? Math.round((p.invoiced_minutes / p.total_minutes) * 100) : 0;
                          return (
                            <div key={p.project_id} className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-slate-800 truncate pr-2">
                                  {p.project_name}
                                </span>
                                <span className="text-xs font-mono text-slate-600 flex-shrink-0">
                                  {fmtHours(totalH)}h
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Progress
                                  value={invoicedPct}
                                  className="h-1.5 flex-1 [&>div]:bg-emerald-500"
                                />
                                <span className="text-[10px] text-slate-400 w-8 text-right">{invoicedPct}%</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">Keine Zeiteinträge im Zeitraum</p>
                    )}
                  </div>

                  {/* Daily Hours Chart */}
                  <div className="md:col-span-7">
                    <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">Stunden pro Tag</h4>
                    {timeStats.daily_series?.length > 0 ? (
                      <div className="h-[180px]" data-testid="time-daily-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={timeStats.daily_series} barCategoryGap="15%">
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                            <XAxis
                              dataKey="date"
                              axisLine={false}
                              tickLine={false}
                              tick={{ fontSize: 9, fill: "#94a3b8" }}
                              tickFormatter={(v) => {
                                const parts = v.split("-");
                                return parts.length === 3 ? `${parts[2]}.${parts[1]}.` : v;
                              }}
                            />
                            <YAxis
                              axisLine={false}
                              tickLine={false}
                              tick={{ fontSize: 10, fill: "#94a3b8" }}
                              tickFormatter={(v) => `${v}h`}
                              width={35}
                            />
                            <RechartsTooltip
                              content={({ active, payload, label }) => {
                                if (!active || !payload?.length) return null;
                                return (
                                  <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2 text-xs">
                                    <p className="font-medium text-slate-900">{label}</p>
                                    <p className="text-slate-600">{payload[0]?.value}h</p>
                                  </div>
                                );
                              }}
                            />
                            <Bar dataKey="hours" fill="#6366f1" radius={[3, 3, 0, 0]} name="Stunden" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">Keine Tageswerte verfügbar</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">Keine Zeitdaten verfügbar</p>
            )}
          </CardContent>
        </Card>

        {/* Row 5: Projects + Weekly Time */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6">
          {/* Project Overview */}
          <div className="md:col-span-6">
            <Card className="h-full" data-testid="projects-card">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                    Top Projekte
                  </h3>
                  <a href="/projects" className="text-xs text-blue-600 hover:underline" data-testid="projects-link">
                    Alle anzeigen
                  </a>
                </div>
                {metrics.topProjects.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <p className="text-sm text-slate-500">Keine Projekte mit Zeiteinträgen</p>
                    <Button variant="outline" size="sm" className="mt-3 text-xs" asChild>
                      <a href="/projects?create=1">Projekt anlegen</a>
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="projects-list">
                    {metrics.topProjects.map((p) => (
                      <div key={p.id} className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-800 truncate pr-2">
                            {p.name}
                          </span>
                          <span className="text-xs font-mono text-slate-600 flex-shrink-0">
                            {fmtHours(p.trackedHours)}h
                            {p.budget_hours ? ` / ${p.budget_hours}h` : ""}
                          </span>
                        </div>
                        {p.budgetPercent !== null && (
                          <Progress
                            value={Math.min(p.budgetPercent, 100)}
                            className={`h-1.5 ${
                              p.budgetPercent > 100
                                ? "[&>div]:bg-rose-500"
                                : p.budgetPercent > 80
                                ? "[&>div]:bg-amber-500"
                                : "[&>div]:bg-emerald-500"
                            }`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Weekly Time Summary — only for current year */}
          <div className="md:col-span-6">
            <Card className="h-full" data-testid="weekly-time-card">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                    {isCurrentYear ? "Diese Woche" : `Kein Wochenüberblick für ${selectedYear}`}
                  </h3>
                  {isCurrentYear && (
                    <span className="text-xs font-mono text-slate-600" data-testid="week-total">
                      {fmtHours(metrics.weekTotal)}h gesamt
                    </span>
                  )}
                </div>
                {isCurrentYear && metrics.weekDays.length > 0 ? (
                  <>
                    <div className="grid grid-cols-7 gap-1.5" data-testid="weekly-grid">
                      {metrics.weekDays.map((d) => {
                        const maxH = Math.max(...metrics.weekDays.map((dd) => dd.hours), 1);
                        const barH = Math.max((d.hours / maxH) * 100, d.hours > 0 ? 8 : 0);
                        return (
                          <div key={d.day} className="flex flex-col items-center gap-1">
                            <div className="relative w-full h-24 flex items-end justify-center">
                              <div
                                className={`w-full max-w-[28px] rounded-t-md transition-all ${
                                  d.isToday
                                    ? "bg-emerald-500"
                                    : d.hours > 0
                                    ? "bg-slate-300"
                                    : "bg-slate-100"
                                }`}
                                style={{ height: `${barH}%` }}
                              />
                            </div>
                            <span
                              className={`text-[10px] font-mono ${
                                d.hours > 0 ? "text-slate-700" : "text-slate-400"
                              }`}
                            >
                              {d.hours > 0 ? `${fmtHours(d.hours)}h` : "-"}
                            </span>
                            <span
                              className={`text-[10px] font-medium ${
                                d.isToday ? "text-emerald-700" : "text-slate-500"
                              }`}
                            >
                              {d.day}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                      <a
                        href="/time-tracking"
                        className="text-xs text-blue-600 hover:underline"
                        data-testid="time-tracking-link"
                      >
                        Zur Zeiterfassung
                      </a>
                      {metrics.weekDays.some((d) => d.unbilled > 0) && (
                        <span className="text-xs text-amber-600">
                          {fmtHours(metrics.weekDays.reduce((s, d) => s + d.unbilled, 0))}h offen
                        </span>
                      )}
                    </div>
                  </>
                ) : !isCurrentYear ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <CalendarDays className="h-8 w-8 text-slate-300 mb-2" />
                    <p className="text-sm text-slate-500">Der Wochenüberblick ist nur für das aktuelle Jahr verfügbar.</p>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Mobile Quick Actions */}
        <div className="sm:hidden grid grid-cols-3 gap-2" data-testid="mobile-quick-actions">
          <Button variant="outline" className="gap-1.5 text-xs h-10 flex-col py-2" asChild>
            <a href="/invoices?create=1">
              <FileText className="h-4 w-4" />
              Rechnung
            </a>
          </Button>
          <Button variant="outline" className="gap-1.5 text-xs h-10 flex-col py-2" asChild>
            <a href="/finances?create=expense">
              <Receipt className="h-4 w-4" />
              Ausgabe
            </a>
          </Button>
          <Button variant="outline" className="gap-1.5 text-xs h-10 flex-col py-2" asChild>
            <a href="/time-tracking?create=1">
              <Clock className="h-4 w-4" />
              Zeit
            </a>
          </Button>
        </div>
      </div>
    </>
  );
}
